/**
 * bagclub-login-href.js
 *
 * Rewrites navigation login link return_to values to the current page.
 *
 * Background: Shopify's /customer_authentication/login?return_to=<path>
 * properly returns customers to a given page after login. But menu links
 * configured in the Shopify admin can only carry a static URL — they
 * can't carry a dynamic return_to set to the current page.
 *
 * This script bridges that gap using the same label-matching pattern as
 * the existing "Nav Button Access" header block. The merchant adds a
 * "Redirect Link" header block, enters the exact text label of the nav
 * link to target (e.g., "Already A Subscriber? Log In"), and this script:
 *
 * 1. Reads the configured label(s) from hidden marker spans in the header
 * 2. Normalizes the labels (lowercase, strip non-alphanumerics) for
 *    flexible matching that ignores special characters and emojis
 * 3. Finds <a> elements whose text content matches a configured label
 * 4. Rewrites their href's return_to to the current page path
 *
 * Only rewrites anchors whose href contains '/customer_authentication/login'
 * — so it won't accidentally modify unrelated nav links that happen to
 * share text with a configured target.
 */
(function () {
  'use strict';

  const LOGIN_PATH = '/customer_authentication/login';
  const CONFIG_SELECTOR = '.bagclub-redirect-link-config';

  // Normalize text for matching: lowercase, strip everything that isn't
  // a letter/number. Matches the case-insensitive, special-char-ignoring
  // pattern used by the existing Nav Button Access feature.
  function normalize(text) {
    if (!text) return '';
    return String(text).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function getConfiguredLabels() {
    const markers = document.querySelectorAll(CONFIG_SELECTOR);
    const labels = [];
    markers.forEach(function (marker) {
      const raw = marker.getAttribute('data-redirect-link-text');
      const normalized = normalize(raw);
      if (normalized) labels.push(normalized);
    });
    return labels;
  }

  function rewriteAnchor(anchor) {
    const href = anchor.getAttribute('href');
    if (!href || href.indexOf(LOGIN_PATH) === -1) return;

    // Skip rewrite on home page — the merchant's default return_to=/ is
    // already correct, and rewriting to "/" is a no-op.
    if (window.location.pathname === '/' || window.location.pathname === '') {
      return;
    }

    const currentPath = window.location.pathname + window.location.search;
    const newHref = LOGIN_PATH + '?return_to=' + encodeURIComponent(currentPath);
    anchor.setAttribute('href', newHref);
  }

  function rewriteAll() {
    const labels = getConfiguredLabels();
    if (labels.length === 0) return;

    // Find all anchors that point at the login path. We only consider
    // these for label matching — narrows the search and prevents accidental
    // rewriting of unrelated links.
    const candidates = document.querySelectorAll('a[href*="' + LOGIN_PATH + '"]');

    candidates.forEach(function (anchor) {
      const text = normalize(anchor.textContent);
      if (!text) return;

      // Exact normalized match — if a label normalizes to the same string
      // as the anchor's text content, treat it as a target. This is
      // tighter than substring matching and avoids accidental matches.
      if (labels.indexOf(text) !== -1) {
        rewriteAnchor(anchor);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rewriteAll);
  } else {
    rewriteAll();
  }

  // Some menu systems (mobile drawers, lazy-loaded mega-menus) may insert
  // link DOM after initial load. Observe and rewrite as needed.
  const observer = new MutationObserver(function (mutations) {
    let shouldRescan = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        // Only rescan if the added subtree contains a candidate anchor —
        // avoids running the full pass on every minor DOM mutation.
        if (
          (node.tagName === 'A' && node.getAttribute('href') && node.getAttribute('href').indexOf(LOGIN_PATH) !== -1) ||
          (node.querySelector && node.querySelector('a[href*="' + LOGIN_PATH + '"]'))
        ) {
          shouldRescan = true;
          break;
        }
      }
      if (shouldRescan) break;
    }
    if (shouldRescan) rewriteAll();
  });

  function startObserving() {
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }
})();
